import { computed, ref, watch, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useMq } from 'vue3-mq'
import { useStore } from '@web/store'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import { getUrlWithAuthParams } from '@web/common/Utils'
import SubscriptionApi from '@web/api/modules/Subscription'
import Logger from '@web/common/Logger'
import useEmbedMode from '@web/composition/useEmbedMode'
import {
  paymentFailureBannerView,
  paymentFailureBannerResumeClick,
  paymentFailureBannerDeclineClick,
  paymentFailureBannerCloseClick,
  type PaymentFailureBannerState
} from '@web/services/Analytics/events/paymentFailureWarning'

const DISMISS_PREFIX = 'paymentFailureWarning:dismissed:'

// TODO MOB-408: временный режим для приёмки вёрстки. Бэк готов, но плашка есть
// только у подписок, отменившихся после выката, — до бэкфилла состояние на
// локалке и тестовых аккаунтах иначе не воспроизвести. Удалить после бэкфилла.
const DEMO_QUERY_PARAM = 'paymentWarningDemo'

// Легаси-раскладка Page.vue позиционирует контент абсолютно относительно
// вьюпорта и отступ под полоску игнорирует — полоска легла бы поверх контента.
const LEGACY_ABSOLUTE_PAGES: string[] = [
  PAGE_NAME.VIDEO_CONSULTATIONS,
  PAGE_NAME.VIDEO_CONSULTATIONS_STREAM,
  PAGE_NAME.VIDEO_CONSULTATION,
  PAGE_NAME.VIDEO_CONSULTATION_STREAM
]

// Счётчик скрытий — модульный, а не локальный: баннер смонтирован сразу в
// нескольких вьюхах, и клик по «×» должен погасить все копии, а не только ту,
// в которой нажали. Сама правда живёт в sessionStorage, тик лишь заставляет
// computed пересчитаться (иначе чтение хранилища для Vue нереактивно).
const dismissTick = ref(0)

// Развёрнутость мобильной полоски. Модульная, а не локальная: композабл может
// быть вызван из нескольких мест, и состояние должно быть общим.
const expanded = ref(false)

function readDismissed (userId: number | string): boolean {
  try {
    return Boolean(sessionStorage.getItem(`${DISMISS_PREFIX}${userId}`))
  } catch {
    // Приватный режим или запрет хранилища — считаем, что не скрыт.
    return false
  }
}

function writeDismissed (userId: number | string): void {
  try {
    sessionStorage.setItem(`${DISMISS_PREFIX}${userId}`, '1')
  } catch {
    // Хранилище недоступно: скрытие проживёт до перезагрузки страницы,
    // но клик всё равно должен сработать, а не упасть.
  }
  dismissTick.value += 1
}

export default function usePaymentFailureWarning (): {
  isVisible: ComputedRef<boolean>
  canResume: ComputedRef<boolean>
  canDecline: ComputedRef<boolean>
  subscription: ComputedRef<Subscription | undefined>
  onResume: () => void
  onDecline: () => void
  onClose: () => void
  trackView: () => void
  isExpanded: ComputedRef<boolean>
  onExpand: () => void
  state: ComputedRef<PaymentFailureBannerState>
  } {
  const store = useStore()
  const route = useRoute()
  const mq = useMq()
  const { isEmbedded } = useEmbedMode()

  const isOnOnboarding = computed<boolean>(() => route.name === PAGE_NAME.ONBOARDING)

  // Легаси-раскладка Page.vue (см. LEGACY_ABSOLUTE_PAGES) позиционирует контент
  // абсолютно относительно вьюпорта — отступ под полоску она игнорирует.
  const isOnLegacyAbsolutePage = computed<boolean>(
    () => LEGACY_ABSOLUTE_PAGES.includes(route.name as string))

  const isDemo = computed<boolean>(() => route.query[DEMO_QUERY_PARAM] === '1')

  const userId = computed<number | string>(() => store.getters['user/user']?.id ?? 'anon')

  const isDismissed = computed<boolean>(() => {
    // Тик читается первым операндом, чтобы computed от него зависел: чтение
    // sessionStorage для Vue нереактивно, и без этого баннер не погас бы до
    // перерисовки. Счётчик только растёт, так что условие всегда истинно и
    // результат определяется хранилищем.
    return dismissTick.value >= 0 && readDismissed(userId.value)
  })

  // Какую подписку показать, решает бэк: billingNotice заполнен ровно у одной,
  // у остальных null. Наличие объекта и означает «показать плашку».
  //
  // Если объект всё же придёт у нескольких — берём самую свежую, с наибольшим id.
  // Это страховка, а не альтернативная логика выбора: порядок в ответе API — это
  // порядок вставки в Map стора, он ни к чему не привязан, и «первая попавшаяся»
  // отправила бы на форму продления произвольной подписки.
  const subscription = computed<Subscription | undefined>(() => {
    if (!store.getters['subscription/isLoaded']) return undefined
    const list: Subscription[] = store.getters['subscription/subscriptions'] ?? []
    return list
      .filter(item => Boolean(item.raw.billingNotice))
      .reduce<Subscription | undefined>(
        (latest, item) => (!latest || item.id > latest.id ? item : latest),
        undefined
      )
  })

  const billingNotice = computed<BillingNotice | undefined>(
    () => subscription.value?.raw.billingNotice ?? undefined)

  const isOnSubscriptionsPage = computed<boolean>(() => route.name === PAGE_NAME.SUBSCRIPTIONS)

  const isVisible = computed<boolean>(() => {
    if (isDismissed.value) return false
    // На странице подписок тот же призыв уже даёт реактивационная плашка MOB-301.
    if (isOnSubscriptionsPage.value) return false
    // Онбординг — линейная воронка на весь экран; посторонний призыв уводит
    // из сценария.
    if (isOnOnboarding.value) return false
    if (isOnLegacyAbsolutePage.value) return false
    // Во встроенном режиме обвязку рисует контейнер — и полоску тоже.
    if (isEmbedded.value) return false
    // Демо-режим форсит показ на фиктивных данных — им принимают вёрстку.
    if (isDemo.value) return true
    // Гейта по проекту нет: плашка показывается всем школам (решение продукта
    // 11.09.2026, отменяет ограничение «только any» от 2026-08-31).
    return Boolean(subscription.value)
  })

  // На проде resume.available будет false примерно у половины отменённых
  // подписок: бэк гейтит его существующим canRenew(), который ложен, пока не
  // истёк оплаченный период. Плашка при этом рендерится — с «Не продлевать».
  const canResume = computed<boolean>(() => {
    if (isDemo.value) return true
    return billingNotice.value?.actions.resume.available === true
  })

  const canDecline = computed<boolean>(() => {
    if (isDemo.value) return true
    return billingNotice.value?.actions.decline.available === true
  })

  function meta (): { subscriptionId: number; serviceId: number } {
    return {
      subscriptionId: subscription.value?.id ?? 0,
      serviceId: subscription.value?.serviceId ?? 0
    }
  }

  const isExpanded = computed<boolean>(() => expanded.value)

  const state = computed<PaymentFailureBannerState>(() => {
    if (mq.current === 'desktop') return 'desktop'
    return expanded.value ? 'expanded' : 'collapsed'
  })

  // ТЗ: развёрнутое состояние не переживает навигацию, сама плашка — переживает.
  watch(() => route.fullPath, () => { expanded.value = false })

  // Разворот полоски события аналитики не шлёт: отдельное событие на этот шаг
  // заводилось и снято решением продукта 11.09.2026. Состояние, в котором
  // пользователь нажал кнопку, по-прежнему видно — его несёт параметр state.
  function onExpand (): void {
    expanded.value = true
  }

  function trackView (): void {
    if (isDemo.value || !subscription.value) return
    // Имя параметра историческое: воронка на can_review уже построена, менять
    // его ради переезда контракта дороже, чем оставить. Смысл тот же — был ли
    // у показа путь к возврату.
    paymentFailureBannerView({ ...meta(), canReview: canResume.value, state: state.value })
  }

  function onResume (): void {
    if (isDemo.value || !subscription.value) return
    // URL приходит абсолютным из контракта — собирать его на фронте больше не
    // нужно. Доклеиваем только utm-метки, разделитель выбираем по наличию query.
    // Гейт именно по available: url бэк присылает всегда, даже когда действие
    // недоступно, так что проверки одного лишь наличия ссылки мало.
    if (!canResume.value) return
    const url = billingNotice.value?.actions.resume.url
    if (!url) return
    paymentFailureBannerResumeClick({ ...meta(), state: state.value })
    const separator = url.includes('?') ? '&' : '?'
    window.open(
      getUrlWithAuthParams(`${url}${separator}utm_source=lk&utm_medium=payment_failure_banner`),
      '_system'
    )
  }

  function onDecline (): void {
    const target = subscription.value
    if (!isDemo.value && target) {
      paymentFailureBannerDeclineClick({ ...meta(), state: state.value })
      // Ответ ручки нового состояния не возвращает, поэтому гасим плашку сразу,
      // не дожидаясь её: к следующей загрузке списка billingNotice уже пропадёт.
      // Сбой ручки скрытие не отменяет — иначе пользователь остался бы с плашкой,
      // на которую он уже ответил. Повторный вызов на бэке безопасен.
      SubscriptionApi.declineRenewal(target.id).catch(error => {
        Logger.error(`MOB-408: decline-renewal failed for subscription ${target.id}`, error)
      })
    }
    writeDismissed(userId.value)
  }

  function onClose (): void {
    if (!isDemo.value && subscription.value) paymentFailureBannerCloseClick({ ...meta(), state: state.value })
    writeDismissed(userId.value)
  }

  return {
    isVisible,
    canResume,
    canDecline,
    subscription,
    onResume,
    onDecline,
    onClose,
    trackView,
    isExpanded,
    onExpand,
    state
  }
}
